import { TaskCategoryDto, WorkloadTask } from 'shovel-lib/types';

/**
 * A category's time (Petr, 2026-10-28): the whole estimate each of its tasks takes. Set in the Task Estimator, in
 * Course details, or by the question asked when a task of it is completed; a task given the category with no time of
 * its own takes it. shovel-lib's category type predates it.
 */
export type TimedTaskCategory = TaskCategoryDto & { defaultMinutes?: number | null };

// A category's name cut so a line naming it stays one line ("Also for 12 other Interactive Exerc…").
export const shortCategoryName = (name: string) => (name.length > 18 ? `${name.slice(0, 17).trimEnd()}…` : name);

export const categoryMinutesOf = (category?: TaskCategoryDto | null): number | null =>
  (category as TimedTaskCategory | null | undefined)?.defaultMinutes ?? null;

/** A course's category as the store has it, with its latest default. */
export const findCourseCategory = (
  courses: { id: number; taskCategories?: TaskCategoryDto[] }[] | undefined,
  courseId: number,
  categoryId: number
): TaskCategoryDto | undefined =>
  courses?.find(course => course.id === courseId)?.taskCategories?.find(category => category.id === categoryId);

/**
 * Sent with a completed task, while some of its category is still to do: whether to use the time it took for those.
 * `newMinutes` is that time to 5 minutes.
 */
export type CategorySuggestion = {
  taskTitle: string;
  categoryId: number;
  courseId: number;
  courseName: string;
  categoryName: string;
  emoji?: string | null;
  // The category's time it took 20% or more longer or shorter than: its default, else the one most of its unfinished
  // tasks share; null when it has neither, and then it's asked whatever the task took.
  currentMinutes: number | null;
  newMinutes: number;
  remainingCount: number;
  // The session whose popup asks it, turned into the question; an overlay asks it otherwise (askInPopup).
  inPopup?: number;
};

// onlyUnestimated: of the tasks still to do, only those with no time yet (a first estimate's "Also for 12 other …").
export type CategoryMinutesRequest = { minutes: number | null; applyToUnfinished: boolean; onlyUnestimated?: boolean };

// skippedReadings: the category's readings linked to a book still to do, which a re-estimate leaves to their pages.
export type CategoryMinutesResponse = { category: TimedTaskCategory; tasks: WorkloadTask[]; skippedReadings?: number };

/** A task's category, as a first estimate's offer needs it. */
export type RowCategory = { id: number; name: string; courseId: number };

/** "Also for 12 other Homework" on a task's first estimate: its category, and how many others in it have no time. */
export type FirstEstimateOffer = { categoryId: number; courseId: number; name: string; others: number };

/**
 * Which of these unestimated tasks offer their pick to the rest of their category (Petr, 2026-10-29): the first one of
 * each category with no default yet whose other tasks have no time either. Only the first, so the list doesn't repeat
 * the box down every card of a category. `unestimatedCounts`: by category, its tasks with no time, these included.
 */
export const firstEstimateOffers = (
  taskIds: number[],
  rowCategories: Record<number, RowCategory>,
  hasDefault: (category: RowCategory) => boolean,
  unestimatedCounts: Record<number, number>
): Record<number, FirstEstimateOffer> => {
  const offers: Record<number, FirstEstimateOffer> = {};
  const seen = new Set<number>();
  taskIds.forEach(taskId => {
    const category = rowCategories[taskId];
    if (!category || seen.has(category.id)) return;
    seen.add(category.id);
    const others = (unestimatedCounts[category.id] || 0) - 1;
    if (others > 0 && !hasDefault(category)) {
      offers[taskId] = { categoryId: category.id, courseId: category.courseId, name: category.name, others };
    }
  });
  return offers;
};
